"use client";

import Image from "next/image";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { useDeferredValue, useEffect, useRef, useState, useTransition } from "react";
import { formatPrice, pickBestProductImage } from "@/lib/catalog";

type SearchSuggestionPayload = {
  categories: Array<{
    slug: string;
    name: string;
    productCount: number;
  }>;
  products: Array<{
    slug: string;
    name: string;
    sku: string | null;
    price: number;
    primaryImage: string | null;
    imageAltText?: string | null;
    images: string[];
  }>;
};

const TEXT = {
  placeholder: "\u041f\u043e\u0438\u0441\u043a\u0020\u0442\u043e\u0432\u0430\u0440\u043e\u0432\u0020\u0438\u0020\u043a\u0430\u0442\u0435\u0433\u043e\u0440\u0438\u0439",
  search: "\u041f\u043e\u0438\u0441\u043a",
  results: "\u0420\u0435\u0437\u0443\u043b\u044c\u0442\u0430\u0442\u044b\u0020\u043f\u043e\u0438\u0441\u043a\u0430",
  categories: "\u041a\u0430\u0442\u0435\u0433\u043e\u0440\u0438\u0438",
  products: "\u0422\u043e\u0432\u0430\u0440\u044b",
  items: "\u0442\u043e\u0432\u0430\u0440\u043e\u0432",
  code: "\u041a\u043e\u0434",
  productFromCatalog: "\u0422\u043e\u0432\u0430\u0440\u0020\u0438\u0437\u0020\u043a\u0430\u0442\u0430\u043b\u043e\u0433\u0430",
  showAll: "\u0421\u043c\u043e\u0442\u0440\u0435\u0442\u044c\u0020\u0432\u0441\u0435\u0020\u0440\u0435\u0437\u0443\u043b\u044c\u0442\u0430\u0442\u044b"
} as const;

function SearchIcon() {
  return (
    <svg aria-hidden="true" viewBox="0 0 24 24">
      <circle cx="11" cy="11" fill="none" r="6.8" stroke="currentColor" strokeWidth="1.8" />
      <path d="m16.2 16.2 3.6 3.6" fill="none" stroke="currentColor" strokeLinecap="round" strokeWidth="1.8" />
    </svg>
  );
}

export function HeaderSearch() {
  const router = useRouter();
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [query, setQuery] = useState("");
  const deferredQuery = useDeferredValue(query);
  const [suggestions, setSuggestions] = useState<SearchSuggestionPayload>({
    categories: [],
    products: []
  });
  const [isOpen, setIsOpen] = useState(false);
  const [isPending, startTransition] = useTransition();

  useEffect(() => {
    if (deferredQuery.trim().length < 2) {
      setSuggestions({ categories: [], products: [] });
      return;
    }

    const controller = new AbortController();
    const timer = window.setTimeout(async () => {
      try {
        const response = await fetch(`/api/search/suggest?q=${encodeURIComponent(deferredQuery.trim())}`, {
          signal: controller.signal
        });

        if (!response.ok) {
          return;
        }

        const payload = (await response.json()) as SearchSuggestionPayload;
        setSuggestions(payload);
        setIsOpen(true);
      } catch {
        // keep last successful suggestions
      }
    }, 140);

    return () => {
      controller.abort();
      window.clearTimeout(timer);
    };
  }, [deferredQuery]);

  useEffect(() => {
    function handlePointerDown(event: MouseEvent) {
      if (!containerRef.current?.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }

    window.addEventListener("mousedown", handlePointerDown);
    return () => window.removeEventListener("mousedown", handlePointerDown);
  }, []);

  function submitSearch() {
    const nextQuery = query.trim();
    if (!nextQuery) {
      router.push("/catalog");
      return;
    }

    setIsOpen(false);
    startTransition(() => {
      router.push(`/catalog?q=${encodeURIComponent(nextQuery)}`);
    });
  }

  const hasSuggestions = suggestions.categories.length > 0 || suggestions.products.length > 0;
  const shouldShowDropdown = isOpen && (hasSuggestions || isPending);

  return (
    <div
      className={`header-search-live ${shouldShowDropdown ? "header-search-live--open" : ""}`}
      ref={containerRef}
    >
      <form
        action="/catalog"
        className="search-form search-form-retail"
        method="get"
        onSubmit={(event) => {
          event.preventDefault();
          submitSearch();
        }}
      >
        <input
          autoComplete="off"
          className="search-input search-input-retail"
          name="q"
          onChange={(event) => {
            setQuery(event.target.value);
            if (!isOpen) {
              setIsOpen(true);
            }
          }}
          onFocus={() => {
            if (hasSuggestions) {
              setIsOpen(true);
            }
          }}
          placeholder={TEXT.placeholder}
          type="text"
          value={query}
        />
        <button aria-label={TEXT.search} className="search-submit search-submit-retail" type="submit">
          <SearchIcon />
        </button>
      </form>

      {shouldShowDropdown ? (
        <div className="header-search-dropdown">
          <div className="header-search-status">{TEXT.results}</div>

          {suggestions.categories.length > 0 ? (
            <section className="header-search-group">
              <div className="header-search-title">{TEXT.categories}</div>
              <div className="header-search-links">
                {suggestions.categories.map((category) => (
                  <Link
                    className="header-search-category"
                    href={`/category/${category.slug}`}
                    key={category.slug}
                    onClick={() => setIsOpen(false)}
                  >
                    <strong>{category.name}</strong>
                    <span>
                      {category.productCount} {TEXT.items}
                    </span>
                  </Link>
                ))}
              </div>
            </section>
          ) : null}

          {suggestions.products.length > 0 ? (
            <section className="header-search-group">
              <div className="header-search-title">{TEXT.products}</div>
              <div className="header-search-products">
                {suggestions.products.map((product) => (
                  <Link
                    className="header-search-product"
                    href={`/product/${product.slug}`}
                    key={product.slug}
                    onClick={() => setIsOpen(false)}
                  >
                    <Image
                      alt={product.imageAltText?.trim() || product.name}
                      height={72}
                      loading="lazy"
                      sizes="72px"
                      src={pickBestProductImage(product.primaryImage, product.images)}
                      width={72}
                    />
                    <div>
                      <strong>{product.name}</strong>
                      <span>
                        {product.sku ? `${TEXT.code} ${product.sku}` : TEXT.productFromCatalog}
                      </span>
                    </div>
                    <em>{formatPrice(product.price)}</em>
                  </Link>
                ))}
              </div>
            </section>
          ) : null}

          <button className="header-search-all" onClick={submitSearch} type="button">
            {TEXT.showAll}
          </button>
        </div>
      ) : null}
    </div>
  );
}
